/**
 * Authored Conductive Dough STEM Mission pages.
 *
 * Nine-phase Saras STEM Mission: Prelude -> Wonder -> Explore -> Understand ->
 * Apply -> Test & Improve -> Explain -> Reflect -> Grow.
 *
 * This file is a mission conversion of the existing Summer Camp Journey.
 * The original Journey remains separate and unchanged.
 *
 * This is data/configuration consumed by JourneyPageRenderer.
 */
const CONDUCTIVE_DOUGH_MISSION_PAGES = {
  "conductive-dough-prelude": {
    "step_id": "conductive-dough-prelude",
    "title": "The Night Glow City Went Dark",
    "subtitle": "Open the Glow City Emergency Case",
    "phase": "Prelude",
    "story": "Enter Glow City just before the Festival of Light. Observe the blackout scene and the four clues without deciding yet which electrical explanation is correct.",
    "self_guided": {
      "title": "Welcome to Glow City",
      "say": "The Festival of Light is dark. Let us investigate the clues before claiming we know what caused the blackout.",
      "do_now": "Watch or read the blackout story, then open the Master Sculptor’s message and reveal the four clues. Look at the last working sculpture if you want more visual evidence. Keep the kit closed for now; there is no physical build in this phase.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You opened the Master Sculptor’s blackout message and revealed the four clues. We have an emergency lead, but we have not tested what caused the lights to fail.",
      "next_preview": "Next, choose one possible cause and start a case file that separates visible evidence from your own explanation.",
      "welcome": "Welcome, Future Engineer! I’m Professor Swan. Glow City’s lights have gone dark. We’ll investigate the clues, explore the 3D models, and test our ideas carefully.",
      "materials": [
        "Supplied conductive dough and insulating dough",
        "Switched battery holder made for four AA batteries",
        "Specified AA batteries, LEDs, and supplied leads",
        "Clear, dry work surface and the supplied kit instructions"
      ],
      "safety": [
        "The kit dough is not food; do not add water or other liquids.",
        "Use only the supplied four-AA holder and approved parts; keep the switch off before changes.",
        "Ask an adult if sharp, damaged, or hot parts need handling. Use the optional buzzer only if it is included and permitted by the supplied kit instructions; otherwise skip it."
      ],
      "parent_note": "Professor Swan guides the supplied four-AA kit and standard low-voltage experiments. An adult can help with sharp, damaged, or overheated components or physical tasks the child cannot safely complete. The child makes the scientific predictions, observations, and decisions."
    },
    "self_guided_block_guidance": {
      "glow-city-story-prelude": {
        "title": "Enter Glow City",
        "try_now": "Watch or read the blackout report; describe the clues before deciding what failed."
      }
    },
    "blocks": [
      {
        "id": "glow-city-story-prelude",
        "type": "story_prelude",
        "eyebrow": "BEFORE THE INVESTIGATION",
        "title": "Enter the Great Glow City Blackout",
        "prompt": "Watch what happened in Glow City, then read the full emergency report before beginning the investigation.",
        "video": {
          "title": "The Great Glow City Blackout",
          "path": "conductive-dough/prelude/glow_city_story_v1.mp4",
          "poster": "conductive-dough/prelude/glow_city_story_poster.jpg",
          "caption": "This story introduces the mystery. It does not explain the science or reveal the solution."
        },
        "reading": {
          "eyebrow": "SARAS-3D STEM MISSION — GLOW CITY",
          "title": "Glow City Emergency Report",
          "content": [
            {
              "type": "paragraph",
              "text": "By the time this report reaches you, the Festival of Light has been stopped."
            },
            {
              "type": "paragraph",
              "text": "The streets of Glow City are still crowded, but no one is celebrating. The musicians have lowered their instruments. Families are standing beneath dark arches and silent sculptures. The festival clock remains frozen at one minute before opening."
            },
            {
              "type": "paragraph",
              "text": "Glow City is famous for artwork that is soft enough to shape by hand and bright enough to glow after sunset. Every year, artists build dragons, flowers, towers, moons, creatures, and winding pathways of light. This year, the Master Electro-Sculptor promised something larger than ever: when the Beacon Tower lit, every sculpture across the city would glow in a wave."
            },
            {
              "type": "paragraph",
              "text": "That wave never came."
            },
            {
              "type": "paragraph",
              "text": "Festival Keeper Luma has collected the first emergency reports. Witnesses agree that the trouble began only moments before the festival. Some lights flickered. Others went dark without warning. Within seconds, the Beacon Tower, the dragon arch, the moon garden, and every other glowing landmark had failed."
            },
            {
              "type": "paragraph",
              "text": "Luma hurried to the Master Sculptor’s workshop, but the room was empty. The tools were arranged neatly, and no sign of a struggle was found. On the worktable stood a small, colorful creature whose green lights had been working during the final inspection. It was now dark too."
            },
            {
              "type": "paragraph",
              "text": "Beside it, Luma discovered a sealed emergency case. Inside were a photograph of the creature while it was still glowing, a short message, and four clue cards."
            },
            {
              "type": "callout",
              "title": "Message from the Master Electro-Sculptor",
              "text": "To the next team of Circuit Sculptors: Do not repair the city by guessing. Observe first. Keep observations separate from explanations. Test one idea at a time, and trust the evidence."
            },
            {
              "type": "numbered_list",
              "title": "THE FOUR CLUES",
              "emphasize": true,
              "items": [
                "BUILD THE ROADS.",
                "BUILD THE WALLS.",
                "FACE THE LIGHT GATES CORRECTLY.",
                "STOP THE SHORTCUT THIEF."
              ]
            },
            {
              "type": "paragraph",
              "text": "No one on the festival council agrees on what the clues mean."
            },
            {
              "type": "paragraph",
              "text": "One worker believes the city’s energy source stopped working. Another thinks there is a break somewhere along the path that the light needs. A third says several lights may have been placed the wrong way. A sculpture assistant reports that parts of two soft landmarks sagged and touched shortly before the blackout. Festival Keeper Luma has written down every statement, but she has not accepted any explanation as the answer."
            },
            {
              "type": "callout",
              "title": "Festival Keeper Luma",
              "text": "An idea is not evidence. We must find a way to test it."
            },
            {
              "type": "numbered_list",
              "title": "CONDITIONS FOR REOPENING THE FESTIVAL",
              "items": [
                "Restore at least one light safely.",
                "Explain what evidence shows why the light works.",
                "Prove that the repaired design can remain glowing without creating a new failure."
              ]
            },
            {
              "type": "paragraph",
              "text": "Your team has been chosen to open the Glow City Emergency Case."
            },
            {
              "type": "paragraph",
              "text": "For now, the engineering kit must remain closed. Begin with the blackout scene and the photograph of the last working creature. Record only what you can actually see. Then write your best current theory and identify which clue supports it."
            },
            {
              "type": "paragraph",
              "text": "Several explanations may still be possible. Strong investigators do not rush to be right. They collect evidence, compare ideas, and revise their thinking when a test reveals something new."
            },
            {
              "type": "paragraph",
              "text": "Outside, the people of Glow City are waiting in the dark. The festival clock still shows one minute before opening."
            },
            {
              "type": "question",
              "text": "What happened to the city’s lights—and which clue should your team investigate first?"
            }
          ]
        }
      },
      {
        "id": "blackout-alert",
        "type": "story_hook",
        "eyebrow": "GLOW CITY EMERGENCY",
        "title": "The Festival of Light cannot open",
        "body": "The sculptures are soft, colorful, and built with tiny lights—but none of them glow. Do not open the kit yet. Strong investigators record what they notice before choosing a cause.",
        "tone": "mystery"
      },
      {
        "id": "blackout-reveal",
        "type": "image_reveal",
        "title": "Open the Master Sculptor’s last message",
        "prompt": "Reveal the blackout scene and read the four clues.",
        "reveal_text": "The clues do not give answers. They name four engineering problems the rescue team must solve: roads, walls, one-way light gates, and electrical shortcuts.",
        "image": {
          "path": "conductive-dough/step-1/glow_city_blackout.png",
          "alt": "A darkened glowing sculpture beside four clues from the missing Master Electro-Sculptor."
        },
        "save_as": "blackout_revealed"
      },
      {
        "id": "evidence-gallery",
        "type": "image_gallery",
        "title": "Inspect the last working landmark",
        "prompt": "This image was captured before the blackout. Record only visible evidence.",
        "save_as": "opening_evidence_image",
        "images": [
          {
            "id": "glowing-creature",
            "label": "Last working Glow City creature",
            "path": "conductive-dough/step-1/glowing_creature_evidence.jpg",
            "caption": "A soft sculpted creature with glowing lights. The image proves that a dough sculpture can contain a working electrical system, but it does not reveal how.",
            "alt": "A colorful dough creature with glowing green lights."
          }
        ]
      },
      {
        "id": "conductive-prelude-completion",
        "type": "completion_gate",
        "incomplete_title": "Open the Glow City case",
        "complete_title": "Emergency case opened",
        "incomplete_prompt": "Reveal the blackout evidence before continuing.",
        "complete_prompt": "You have the emergency evidence. Next, Wonder about what could have caused the blackout.",
        "rules": [
          {
            "field": "blackout_revealed",
            "condition": "truthy"
          }
        ]
      }
    ]
  },
  "conductive-dough-wonder": {
    "step_id": "conductive-dough-wonder",
    "title": "What Caused the Blackout?",
    "subtitle": "Separate Evidence from Explanation",
    "phase": "Wonder",
    "story": "Use visible evidence to choose a testable opening theory. Several causes are still possible, so the goal is not to guess the answer—it is to decide what evidence would distinguish the ideas.",
    "self_guided": {
      "title": "Make a theory you can test",
      "say": "There could be more than one reason a sculpture did not light. Your first idea is a question for evidence, not a final answer.",
      "do_now": "Choose your current explanation, start the case file, and describe what observation could help test your idea.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You chose an initial explanation for the blackout and filled in your case file. That idea is a starting hypothesis—not yet a proven cause.",
      "next_preview": "Next, inspect the virtual materials and two separate dough paths to learn what a complete circuit needs."
    },
    "self_guided_block_guidance": {
      "opening-cause": {
        "title": "Choose an explanation to test",
        "try_now": "Pick the cause you would investigate first, and name evidence you would need."
      }
    },
    "blocks": [
      {
        "id": "opening-cause",
        "type": "choice_cards",
        "title": "What might make every light go dark?",
        "prompt": "Choose your best current theory. You will revise it after collecting evidence.",
        "choices": [
          {
            "id": "battery",
            "label": "The energy source may not be connected or switched on"
          },
          {
            "id": "broken_path",
            "label": "The electrical path may be incomplete"
          },
          {
            "id": "wrong_direction",
            "label": "The light may face the wrong direction"
          },
          {
            "id": "touching_paths",
            "label": "Two paths may be touching and creating a shortcut"
          },
          {
            "id": "material",
            "label": "The soft material may not carry current"
          },
          {
            "id": "need_evidence",
            "label": "Several causes are possible—I need tests"
          }
        ],
        "save_as": "opening_cause_prediction",
        "required": true
      },
      {
        "id": "opening-observations",
        "type": "experiment_record",
        "title": "Start the Glow City case file",
        "prompt": "Record observations separately from explanations.",
        "save_as": "opening_case_file",
        "fields": [
          {
            "id": "observation_one",
            "label": "Observation 1",
            "type": "text",
            "placeholder": "I can see...",
            "min_length": 8
          },
          {
            "id": "observation_two",
            "label": "Observation 2",
            "type": "text",
            "placeholder": "Another clue is...",
            "min_length": 8
          },
          {
            "id": "initial_theory",
            "label": "Initial theory supported by a clue",
            "type": "text",
            "placeholder": "I think... because...",
            "min_length": 20
          }
        ]
      },
      {
        "id": "step1-saras",
        "type": "saras_checkpoint",
        "title": "Investigate the blackout with Saras",
        "suggested_questions": [
          "What is the difference between an observation and a cause?",
          "What tests could separate a battery problem from a path problem?",
          "What can I infer from the glowing creature image?",
          "Help me turn my theory into a testable question."
        ],
        "hidden_context": "The student is beginning The Great Glow City Blackout Conductive Dough Journey. The true concepts are conductive dough, insulating dough, a complete circuit, LED polarity, and short-circuit bypasses. Do not reveal any of those solutions yet. Help the student separate observations from explanations and propose safe tests without opening the kit. Ask one useful question at a time.",
        "reveal_policy": "Preserve the mystery. Do not explain why conductive dough works, LED polarity, insulating dough, or short circuits yet."
      },
      {
        "id": "conductive-wonder-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish your opening Glow City theory",
        "complete_title": "Opening theory ready",
        "incomplete_prompt": "Record evidence separately from your explanation.",
        "complete_prompt": "Your theory is ready to test. Next, Explore the soft roads that can carry current and the complete loop they must form.",
        "rules": [
          {
            "field": "opening_cause_prediction",
            "condition": "answered"
          },
          {
            "field": "opening_case_file",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "conductive-dough-explore": {
    "step_id": "conductive-dough-explore",
    "title": "Find the Roads That Carry Current",
    "subtitle": "Conductive Dough and the Complete Loop",
    "phase": "Explore",
    "story": "Investigate which soft material can carry current, which material should separate paths, and why two conductive roads must stay apart while still forming a complete circuit through the LED.",
    "self_guided": {
      "title": "Find the routes for electricity",
      "say": "Some dough can carry current and some can keep paths separated. First discover the roles of the materials in the virtual lab.",
      "do_now": "Predict the material jobs, explore the virtual kit, trace the two separate conductive paths, and explain the complete route in your own words.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You opened the 3D materials and two-path stages, traced the complete route through the LED, and explained why the conductive dough paths must remain apart.",
      "next_preview": "Next, compare the LED in both directions using 3D stages and the dark-LED evidence."
    },
    "self_guided_block_guidance": {
      "material-prediction": {
        "title": "Predict the material jobs",
        "try_now": "Which supplied material might carry current, and which might keep paths apart?"
      },
      "mainmenu-pwo": {
        "title": "Explore the virtual Glow City kit",
        "try_now": "Open and rotate the 3D kit to identify the components before predicting what each one does."
      },
      "materials-pwo": {
        "title": "Compare the two dough materials",
        "try_now": "Open the 3D Materials Required tray and identify the four-AA holder, wires, LED, conductive dough, and insulating dough. This is a parts inspection, not a conductivity test."
      },
      "step1-pwo": {
        "title": "Investigate the first 3D circuit",
        "try_now": "In 3D Step 1, notice the two separate conductive dough pieces and the gap between them. No LED bridge is installed yet; predict what might connect the paths later."
      },
      "step2-pwo": {
        "title": "Look for a complete loop",
        "try_now": "In 3D Step 2, trace the red battery lead to one dough path and the black lead to the other. The LED is added in Step 3, so do not claim the light path is finished yet."
      }
    },
    "blocks": [
      {
        "id": "step2-story",
        "type": "story_hook",
        "eyebrow": "CLUE 1 — BUILD THE ROADS",
        "title": "Can a soft material behave like a wire?",
        "body": "Conductivity depends on what a material contains, not whether it is hard or soft. Predict before opening the virtual evidence tray.",
        "tone": "mystery"
      },
      {
        "id": "material-prediction",
        "type": "classification",
        "title": "Predict conductors and insulators",
        "prompt": "Classify each material using your current reasoning. These predictions may be revised.",
        "categories": [
          {
            "id": "likely_conductor",
            "label": "Likely to conduct in a circuit"
          },
          {
            "id": "likely_insulator",
            "label": "Likely to resist current"
          },
          {
            "id": "need_test",
            "label": "Need evidence"
          }
        ],
        "items": [
          {
            "id": "metal_clip",
            "label": "Metal connector clip"
          },
          {
            "id": "conductive_dough",
            "label": "Green/red/blue conductive dough from the kit"
          },
          {
            "id": "insulating_dough",
            "label": "White insulating dough from the kit"
          },
          {
            "id": "dry_plastic",
            "label": "Dry plastic container"
          },
          {
            "id": "rubber",
            "label": "Rubber band"
          }
        ],
        "save_as": "material_predictions",
        "required": true
      },
      {
        "id": "mainmenu-pwo",
        "type": "pwo_asset",
        "title": "Enter the Conductive Dough Lab",
        "mission": "Explore the main scene. Identify the energy source, soft circuit paths, and light without beginning assembly.",
        "save_as": "mainmenu_pwo_opened",
        "asset": {
          "id": "conductive-mainmenu",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "mainmenu"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "materials-pwo",
        "type": "pwo_asset",
        "title": "Inspect the virtual materials tray",
        "mission": "Open Materials Required. Compare the conductive dough, insulating dough, LED, connecting wires, and 4-AA battery holder.",
        "save_as": "materials_pwo_opened",
        "asset": {
          "id": "conductive-materials",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "materialsrequired"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "material-gallery",
        "type": "image_gallery",
        "title": "Read the material evidence",
        "prompt": "Compare the PWO tray with the conductor/wall diagram.",
        "save_as": "material_image_examined",
        "images": [
          {
            "id": "materials",
            "label": "Virtual materials tray",
            "path": "conductive-dough/step-2/materials_required.png",
            "caption": "The PWO shows a 4-AA battery holder, LED, leads, conductive dough, and insulating dough.",
            "alt": "Conductive Dough PWO materials tray."
          },
          {
            "id": "roads-walls",
            "label": "Roads and walls model",
            "path": "conductive-dough/step-2/conductive_vs_insulating.png",
            "caption": "Conductive dough provides a current path; insulating dough has high resistance and keeps paths separated.",
            "alt": "Diagram comparing conductive dough as a road and insulating dough as a wall."
          }
        ]
      },
      {
        "id": "material-jobs",
        "type": "matching",
        "title": "Match each material to its main job",
        "prompt": "Use evidence, not color alone.",
        "save_as": "material_jobs",
        "items": [
          {
            "id": "conductive",
            "label": "Conductive dough",
            "options": [
              {
                "id": "carry_current",
                "label": "Provides a soft path through which current can pass"
              },
              {
                "id": "store_energy",
                "label": "Stores the circuit’s chemical energy"
              },
              {
                "id": "block_all",
                "label": "Always blocks current"
              }
            ]
          },
          {
            "id": "insulating",
            "label": "Insulating dough",
            "options": [
              {
                "id": "separate_paths",
                "label": "Keeps conductive paths apart by offering high resistance"
              },
              {
                "id": "power_led",
                "label": "Supplies energy to the LED"
              },
              {
                "id": "create_current",
                "label": "Creates electrical current"
              }
            ]
          },
          {
            "id": "battery",
            "label": "4-AA battery holder",
            "options": [
              {
                "id": "energy_source",
                "label": "Supplies energy and potential difference to the circuit"
              },
              {
                "id": "light_gate",
                "label": "Allows current in only one direction"
              },
              {
                "id": "soft_wire",
                "label": "Acts as the flexible circuit path"
              }
            ]
          }
        ],
        "required": true
      },
      {
        "id": "road-explanation",
        "type": "evidence_response",
        "title": "Explain the first clue",
        "prompt": "Why can conductive dough act like a soft electrical road, while insulating dough is used differently?",
        "placeholder": "Conductive dough can... because... Insulating dough helps by...",
        "save_as": "road_secret_explanation",
        "min_length": 35,
        "required": true
      },
      {
        "id": "step2-saras",
        "type": "saras_checkpoint",
        "title": "Discuss soft conductors with Saras",
        "suggested_questions": [
          "Does a material have to be metal to conduct current?",
          "What evidence distinguishes conductive and insulating dough?",
          "What do dissolved charged particles do in conductive dough?",
          "Why should we avoid saying every ordinary dough behaves the same way?"
        ],
        "hidden_context": "The student is solving the first Glow City clue. You may now explain that this kit’s conductive dough contains water and dissolved charged particles/ions that allow current to pass, while the insulating dough has much higher resistance and is used to separate paths. Do not claim that every ordinary play dough is nonconductive; conductivity depends on ingredients and moisture. Do not move ahead to LED polarity or short circuits.",
        "reveal_policy": "Conductors, insulators, and the high-level role of ions may be explained. Preserve polarity and shortcut discoveries."
      },
      {
        "id": "step3-story",
        "type": "story_hook",
        "eyebrow": "POWER MAP",
        "title": "Every trip needs a way out and a way back",
        "body": "A circuit is not a one-way dead end. Trace the entire loop before adding the light.",
        "tone": "mystery"
      },
      {
        "id": "two-road-prediction",
        "type": "choice_cards",
        "title": "Why are there two separate dough pieces?",
        "prompt": "Choose the explanation that best fits a complete circuit.",
        "choices": [
          {
            "id": "out_return",
            "label": "One connects toward the positive side and one provides a return path to the battery"
          },
          {
            "id": "two_colors",
            "label": "The circuit only works when two colors are used"
          },
          {
            "id": "more_energy",
            "label": "Two pieces create twice as much energy"
          },
          {
            "id": "decoration",
            "label": "They are separated only for decoration"
          },
          {
            "id": "not_sure",
            "label": "I need to trace the loop first"
          }
        ],
        "save_as": "two_road_prediction",
        "required": true
      },
      {
        "id": "step1-pwo",
        "type": "pwo_asset",
        "title": "Place the two conductive paths",
        "mission": "Open Step 1. Notice the gap between the two dough pieces and predict what could bridge it later.",
        "save_as": "pwo_step1_opened",
        "asset": {
          "id": "conductive-step1",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "step1"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "step2-pwo",
        "type": "pwo_asset",
        "title": "Connect the 4-AA battery leads",
        "mission": "Open Step 2. Trace the red lead into one dough piece and the black lead into the other.",
        "save_as": "pwo_step2_opened",
        "asset": {
          "id": "conductive-step2",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "step2"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "two-road-gallery",
        "type": "image_gallery",
        "title": "Inspect the circuit map",
        "prompt": "Compare the PWO stages with the complete-loop diagram.",
        "save_as": "circuit_map_examined",
        "images": [
          {
            "id": "stage1",
            "label": "Two separated paths",
            "path": "conductive-dough/step-3/pwo_step1_two_paths.png",
            "caption": "The two conductive pieces are deliberately separated.",
            "alt": "PWO Step 1 with two separated dough pieces."
          },
          {
            "id": "stage2",
            "label": "Battery leads connected",
            "path": "conductive-dough/step-3/pwo_step2_battery_leads.png",
            "caption": "The red and black leads connect to different conductive pieces.",
            "alt": "PWO Step 2 with battery leads connected to separate dough pieces."
          },
          {
            "id": "loop",
            "label": "Full loop model",
            "path": "conductive-dough/step-3/complete_circuit_loop.png",
            "caption": "The future LED must bridge the two paths so current can pass through it and return to the battery.",
            "alt": "Diagram of a complete conductive dough circuit loop."
          }
        ]
      },
      {
        "id": "loop-sequence",
        "type": "sequence_builder",
        "title": "Trace the complete loop",
        "prompt": "Arrange the events in the order you can follow around the circuit.",
        "save_as": "complete_loop_sequence",
        "items": [
          {
            "id": "battery_positive",
            "label": "Energy source and positive battery connection"
          },
          {
            "id": "first_path",
            "label": "First conductive dough path"
          },
          {
            "id": "working_component",
            "label": "LED bridging the two paths"
          },
          {
            "id": "return_path",
            "label": "Second conductive dough return path"
          },
          {
            "id": "battery_negative",
            "label": "Return connection to the battery"
          }
        ],
        "correct_order": [
          "battery_positive",
          "first_path",
          "working_component",
          "return_path",
          "battery_negative"
        ],
        "correct_feedback": "Yes. You traced one continuous loop through the LED and back to the battery.",
        "retry_feedback": "Follow the path with your finger. The route cannot jump across an air gap."
      },
      {
        "id": "gap-reason",
        "type": "evidence_response",
        "title": "Defend the separation",
        "prompt": "Why must the two conductive dough pieces remain separate before the LED is added?",
        "placeholder": "They must stay separate because...",
        "save_as": "separation_reason",
        "min_length": 25,
        "required": true
      },
      {
        "id": "step3-saras",
        "type": "saras_checkpoint",
        "title": "Trace the circuit with Saras",
        "suggested_questions": [
          "Can you help me trace the whole loop without skipping a connection?",
          "Why is an air gap useful before the LED is inserted?",
          "Does current get used up before returning to the battery?",
          "How is a complete circuit different from stored battery energy?"
        ],
        "hidden_context": "The student is learning the two-path architecture of the Conductive Dough circuit. Explain a complete loop using age-appropriate language. Do not say current is used up. The battery supplies energy; current exists through a complete path. Preserve the LED polarity clue and detailed short-circuit explanation for later steps.",
        "reveal_policy": "You may explain complete/open circuits and outgoing/return connections. Do not reveal the one-way LED rule or shortcut thief yet."
      },
      {
        "id": "conductive-explore-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the soft-circuit investigation",
        "complete_title": "Glow City roads mapped",
        "incomplete_prompt": "Finish the material investigation and complete-loop model.",
        "complete_prompt": "You can trace the soft electrical paths. Next, Understand why the LED is a one-way light gate.",
        "rules": [
          {
            "field": "material_predictions",
            "condition": "completed"
          },
          {
            "field": "mainmenu_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "materials_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "material_jobs",
            "condition": "completed"
          },
          {
            "field": "road_secret_explanation",
            "condition": "min_length",
            "value": 35
          },
          {
            "field": "two_road_prediction",
            "condition": "answered"
          },
          {
            "field": "pwo_step1_opened",
            "condition": "truthy"
          },
          {
            "field": "pwo_step2_opened",
            "condition": "truthy"
          },
          {
            "field": "complete_loop_sequence",
            "condition": "sequence_correct"
          },
          {
            "field": "separation_reason",
            "condition": "min_length",
            "value": 25
          }
        ]
      }
    ]
  },
  "conductive-dough-understand": {
    "step_id": "conductive-dough-understand",
    "title": "Solve the One-Way Light Gate",
    "subtitle": "Use Evidence to Explain LED Polarity",
    "phase": "Understand",
    "story": "Use a safe before-and-after comparison to understand LED polarity and explain why reversing the LED can change whether it lights.",
    "self_guided": {
      "title": "Solve the one-way light mystery",
      "say": "LED orientation matters. Predict what may change before using the virtual experiment to compare the two orientations.",
      "do_now": "Make a polarity prediction, inspect both virtual LED arrangements, and record the difference before doing the science check.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You predicted an LED result, opened both polarity stages, and recorded the virtual comparison. The direction of the LED matters even when the rest of the path looks complete.",
      "next_preview": "Next, use the actual four-AA kit and safety checklist to build and record a first-light test."
    },
    "self_guided_block_guidance": {
      "polarity-prediction": {
        "title": "Predict before swapping",
        "try_now": "Before opening the Step 4 comparison, predict whether swapping the LED direction could change what lights up. Then use the 3D evidence to test your idea."
      },
      "step3-pwo": {
        "title": "Examine the virtual LED",
        "try_now": "In 3D Step 3, examine how the LED bridges the two separate dough paths. Observe which leg touches each path before comparing the reversed arrangement."
      },
      "step4-pwo": {
        "title": "Check the 3D evidence",
        "try_now": "Compare the two virtual arrangements and use what you observe to explain the result in your own words."
      }
    },
    "blocks": [
      {
        "id": "step4-story",
        "type": "story_hook",
        "eyebrow": "CLUE 3 — FACE THE LIGHT GATE",
        "title": "The path looks complete, but the LED stays dark",
        "body": "Before blaming the battery, test the LED orientation. Good troubleshooters change one thing at a time.",
        "tone": "mystery"
      },
      {
        "id": "polarity-prediction",
        "type": "choice_cards",
        "title": "What will happen if the LED legs are swapped?",
        "prompt": "Commit to a prediction before opening Step 4.",
        "choices": [
          {
            "id": "still_light",
            "label": "It will light the same way"
          },
          {
            "id": "go_dark",
            "label": "It will stay dark because the LED blocks current in that direction"
          },
          {
            "id": "brighter",
            "label": "It will become brighter"
          },
          {
            "id": "battery_reverse",
            "label": "The batteries will reverse themselves"
          },
          {
            "id": "need_test",
            "label": "I need an orientation test"
          }
        ],
        "save_as": "polarity_prediction",
        "required": true
      },
      {
        "id": "polarity-gallery",
        "type": "image_gallery",
        "title": "Inspect the one-way gate",
        "prompt": "Compare inserting the LED with swapping its terminals.",
        "save_as": "polarity_image_examined",
        "images": [
          {
            "id": "insert",
            "label": "Insert the LED",
            "path": "conductive-dough/step-4/pwo_step3_insert_led.png",
            "caption": "The LED bridges the two separate conductive paths.",
            "alt": "PWO Step 3 showing an LED ready to be inserted."
          },
          {
            "id": "swap",
            "label": "Swap the terminals",
            "path": "conductive-dough/step-4/pwo_step4_swap_led.png",
            "caption": "The PWO invites the learner to swap the LED terminals and observe the result.",
            "alt": "PWO Step 4 showing the LED terminals being swapped."
          },
          {
            "id": "gate",
            "label": "Polarity evidence diagram",
            "path": "conductive-dough/step-4/led_polarity_gate.png",
            "caption": "In this kit, the longer LED leg connects toward the positive side and the shorter leg toward the negative side.",
            "alt": "Diagram comparing correct and reversed LED orientation."
          }
        ]
      },
      {
        "id": "step3-pwo",
        "type": "pwo_asset",
        "title": "Insert the LED bridge",
        "mission": "Open Step 3. Place the LED so it connects the two conductive paths.",
        "save_as": "pwo_step3_opened",
        "asset": {
          "id": "conductive-step3",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "step3"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "step4-pwo",
        "type": "pwo_asset",
        "title": "Swap the LED terminals",
        "mission": "Open Step 4. Reverse the LED and compare what happens.",
        "save_as": "pwo_step4_opened",
        "asset": {
          "id": "conductive-step4",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "step4"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "dark-led-video",
        "type": "video_asset",
        "title": "Video evidence: The Mystery of the Dark LED",
        "mission": "Watch for the diagnostic sequence and the one-way behavior of the LED. Focus on polarity; the video may show components beyond this physical kit.",
        "save_as": "dark_led_video_opened",
        "asset": {
          "id": "dark-led-video-asset",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7017,
            "tag_id": 30034
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "polarity-record",
        "type": "experiment_record",
        "title": "Record the virtual polarity test",
        "prompt": "Describe what changed when only the LED direction changed.",
        "save_as": "polarity_test_record",
        "fields": [
          {
            "id": "before",
            "label": "Before the swap",
            "type": "choice",
            "choices": [
              {
                "id": "lit",
                "label": "LED lit"
              },
              {
                "id": "dark",
                "label": "LED stayed dark"
              },
              {
                "id": "unclear",
                "label": "Could not tell"
              }
            ]
          },
          {
            "id": "after",
            "label": "After the swap",
            "type": "choice",
            "choices": [
              {
                "id": "lit",
                "label": "LED lit"
              },
              {
                "id": "dark",
                "label": "LED stayed dark"
              },
              {
                "id": "unclear",
                "label": "Could not tell"
              }
            ]
          },
          {
            "id": "rule",
            "label": "Write the one-way rule in your own words",
            "type": "text",
            "placeholder": "An LED works when...",
            "min_length": 20
          }
        ]
      },
      {
        "id": "polarity-check",
        "type": "quiz_question",
        "title": "Glow City diagnostic check",
        "prompt": "A complete circuit has fresh batteries and separated dough paths, but the LED is dark. What is the best first change?",
        "choices": [
          {
            "id": "reverse_led",
            "label": "Switch power off, reverse the LED, and retest"
          },
          {
            "id": "touch_paths",
            "label": "Push the two conductive paths together"
          },
          {
            "id": "change_material",
            "label": "Change the dough without first testing polarity"
          },
          {
            "id": "add_batteries",
            "label": "Add more unapproved batteries"
          }
        ],
        "correct_answer": "reverse_led",
        "save_as": "polarity_diagnostic_check",
        "correct_feedback": "Correct. With the other conditions unchanged, reversing the LED is a focused polarity test.",
        "incorrect_feedback": "Use a safe one-variable test. Do not make the conductive paths touch or add unapproved materials."
      },
      {
        "id": "step4-saras",
        "type": "saras_checkpoint",
        "title": "Diagnose the one-way light gate with Saras",
        "suggested_questions": [
          "Why does an LED work in only one direction?",
          "How can I troubleshoot polarity without changing several things?",
          "Why must power be off before I move the LED?",
          "Can you question my explanation without writing it for me?"
        ],
        "hidden_context": "The student has reached the LED polarity step. Explain that an LED is a diode and allows current primarily in one direction. For this kit, long leg toward positive and short leg toward negative is the practical rule. Encourage power off before adjustment and careful handling of sharp legs. Do not introduce advanced semiconductor physics unless asked. Do not move ahead to short circuits.",
        "reveal_policy": "The LED polarity rule may be fully explained. Preserve the shortcut-thief mechanism for Step 6."
      },
      {
        "id": "conductive-understand-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish the light-gate investigation",
        "complete_title": "LED polarity understood",
        "incomplete_prompt": "Complete the polarity prediction, test, and explanation.",
        "complete_prompt": "You solved the one-way light gate. Next, Apply the rules by restoring the first physical light.",
        "rules": [
          {
            "field": "polarity_prediction",
            "condition": "answered"
          },
          {
            "field": "pwo_step3_opened",
            "condition": "truthy"
          },
          {
            "field": "pwo_step4_opened",
            "condition": "truthy"
          },
          {
            "field": "dark_led_video_opened",
            "condition": "truthy"
          },
          {
            "field": "polarity_test_record",
            "condition": "record_complete"
          },
          {
            "field": "polarity_diagnostic_check",
            "condition": "equals",
            "value": "reverse_led"
          }
        ]
      }
    ]
  },
  "conductive-dough-apply": {
    "step_id": "conductive-dough-apply",
    "title": "Restore the First Light",
    "subtitle": "Build the Physical 4-AA Dough Circuit",
    "phase": "Apply",
    "story": "Use the actual switched four-AA holder, supplied dough, leads, and LED to build one simple, safe, reliable circuit before attempting a larger sculpture.",
    "self_guided": {
      "title": "Restore a single light safely",
      "say": "Use the real four-AA holder and the supplied kit, not a homemade replacement power source. A safe simple circuit comes before a sculpture.",
      "do_now": "Match physical parts to the guide, complete the power-off checklist, and follow the first-light build and test instructions. Ask Professor Swan about unclear connections.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You inventoried your actual four-AA kit, completed the power-off and build checks, and recorded the first physical LED test. Its real result—not a guess—is our evidence.",
      "next_preview": "Next, investigate possible shortcuts, try an insulating wall, and test a larger Glow City landmark."
    },
    "self_guided_block_guidance": {
      "physical-safety": {
        "title": "Power off and check the kit",
        "try_now": "Complete the four-AA kit safety checklist before the first physical test; ask Professor Swan about any unclear step."
      },
      "assembly-pwo": {
        "title": "Follow the virtual assembly guide",
        "try_now": "Examine and rotate the 3D assembly one stage at a time. Then follow the supplied physical kit instructions with the power switched off; ask Professor Swan to explain any step you do not understand."
      }
    },
    "blocks": [
      {
        "id": "step5-story",
        "type": "story_hook",
        "eyebrow": "PHYSICAL KIT RELEASE",
        "title": "Simple and reliable comes before spectacular",
        "body": "Inventory first. Keep the switch OFF while building. Use two separated conductive paths, one correctly oriented LED, and a brief final test.",
        "tone": "mystery"
      },
      {
        "id": "kit-gallery",
        "type": "image_gallery",
        "title": "Match the real kit to the virtual evidence",
        "prompt": "The actual kit uses four AA batteries and includes a buzzer as an optional extension. It does not require a motor.",
        "save_as": "kit_image_examined",
        "images": [
          {
            "id": "actual",
            "label": "Actual Squishy Circuits kit",
            "path": "conductive-dough/step-5/actual_kit.png",
            "caption": "The standard kit includes conductive dough, insulating dough, a switched battery holder, LEDs, and a piezoelectric buzzer.",
            "alt": "Actual Squishy Circuits kit with dough, battery holder, LEDs, and buzzer."
          },
          {
            "id": "labeled",
            "label": "Labeled kit inventory",
            "path": "conductive-dough/step-5/labeled_kit.png",
            "caption": "The battery holder is designed for four AA batteries. The buzzer is optional and not part of the first-light requirement.",
            "alt": "Labeled conductive dough kit components."
          },
          {
            "id": "guide",
            "label": "First-light build guide",
            "path": "conductive-dough/step-5/first_glow_build_guide.png",
            "caption": "Build with power off, test briefly, then switch off before adjustment.",
            "alt": "Five-step guide for the first physical conductive dough LED circuit."
          }
        ]
      },
      {
        "id": "assembly-pwo",
        "type": "pwo_asset",
        "title": "Open the virtual assembly menu",
        "mission": "Use the Step scene as a build companion. Follow only the parts and sequence that match the real 4-AA kit.",
        "save_as": "assembly_pwo_opened",
        "asset": {
          "id": "conductive-assembly",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "step"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "kit-inventory",
        "type": "multi_select",
        "title": "Inventory the physical kit",
        "prompt": "Select each item only after finding it. Ask the teacher about anything missing or damaged.",
        "choices": [
          {
            "id": "four_aa_holder",
            "label": "Battery holder designed for four AA batteries, with switch"
          },
          {
            "id": "four_aa_batteries",
            "label": "Four teacher-approved AA batteries"
          },
          {
            "id": "red_black_leads",
            "label": "Red and black connecting leads/terminals"
          },
          {
            "id": "conductive_dough",
            "label": "Colored conductive dough"
          },
          {
            "id": "insulating_dough",
            "label": "White insulating dough"
          },
          {
            "id": "leds",
            "label": "LED lights with two metal legs"
          },
          {
            "id": "buzzer",
            "label": "Piezoelectric buzzer (optional extension)"
          },
          {
            "id": "clear_workspace",
            "label": "Clean, dry workspace"
          }
        ],
        "save_as": "physical_kit_inventory",
        "min_selections": 7,
        "required": true
      },
      {
        "id": "physical-safety",
        "type": "build_checklist",
        "title": "Power-off build authorization",
        "prompt": "Every item must be confirmed before the first powered test.",
        "save_as": "physical_safety_check",
        "statuses": [
          {
            "id": "complete",
            "label": "Confirmed"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "not_food",
            "label": "We will treat all kit dough as classroom material—not food",
            "help": "Wash hands after use and follow the kit instructions."
          },
          {
            "id": "switch_off",
            "label": "The battery-holder switch stays OFF while inserting wires or LED legs",
            "help": "Power is used only for a brief observation."
          },
          {
            "id": "four_aa_only",
            "label": "We will use only the four teacher-approved AA batteries for this holder",
            "help": "Do not mix battery types or add extra batteries."
          },
          {
            "id": "separate_paths",
            "label": "The two conductive pieces remain separated",
            "help": "Touching paths can create an unwanted low-resistance bypass."
          },
          {
            "id": "led_legs",
            "label": "We will handle sharp LED legs carefully and keep them away from faces",
            "help": "Ask an adult for help with bent or damaged parts."
          },
          {
            "id": "no_liquid",
            "label": "We will not add any unapproved liquid to the kit dough",
            "help": "Use the commercial kit material only as supplied unless the manufacturer explicitly instructs otherwise."
          },
          {
            "id": "stop_problem",
            "label": "We will switch off immediately for heat, smell, leaking batteries, or damaged parts",
            "help": "An adult handles damaged electrical materials."
          }
        ]
      },
      {
        "id": "first-light-build",
        "type": "build_checklist",
        "title": "Build the first Glow City bridge",
        "prompt": "Complete the physical build in order. Switch OFF before every adjustment.",
        "save_as": "first_light_build_check",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "two_pieces",
            "label": "Made two separate conductive dough pieces"
          },
          {
            "id": "leads_separate",
            "label": "Connected red and black leads to different pieces"
          },
          {
            "id": "led_bridge",
            "label": "Inserted one LED so each leg enters a different piece"
          },
          {
            "id": "polarity_checked",
            "label": "Checked the longer and shorter LED legs before power"
          },
          {
            "id": "brief_test",
            "label": "Switched ON briefly, observed the result, then switched OFF"
          }
        ]
      },
      {
        "id": "first-light-record",
        "type": "experiment_record",
        "title": "Record the first physical test",
        "prompt": "A dark LED is still useful evidence. Record what happened before troubleshooting.",
        "save_as": "first_light_test",
        "fields": [
          {
            "id": "result",
            "label": "First result",
            "type": "choice",
            "choices": [
              {
                "id": "bright",
                "label": "LED glowed clearly"
              },
              {
                "id": "dim",
                "label": "LED glowed dimly"
              },
              {
                "id": "dark",
                "label": "LED stayed dark"
              },
              {
                "id": "flicker",
                "label": "LED flickered"
              }
            ]
          },
          {
            "id": "paths",
            "label": "Were the conductive pieces separated?",
            "type": "choice",
            "choices": [
              {
                "id": "yes",
                "label": "Yes"
              },
              {
                "id": "touching",
                "label": "They touched somewhere"
              },
              {
                "id": "unsure",
                "label": "Not sure"
              }
            ]
          },
          {
            "id": "observation",
            "label": "One exact observation",
            "type": "text",
            "placeholder": "I observed...",
            "min_length": 12
          }
        ]
      },
      {
        "id": "step5-saras",
        "type": "saras_checkpoint",
        "title": "Troubleshoot the first light with Saras",
        "suggested_questions": [
          "Give me one safe diagnostic check at a time.",
          "How do I tell a polarity problem from touching dough paths?",
          "What evidence should I record before changing the build?",
          "Why must the switch be off during adjustments?"
        ],
        "hidden_context": "The student is building the first physical conductive dough LED circuit with an actual four-AA battery holder. Prioritize safety: kit dough is not food, no added liquids, switch off before adjustments, use approved AA batteries, and stop for heat/smell/damage. Give one diagnostic check at a time: power/batteries, path separation, LED polarity, lead contact. Do not suggest direct shorts or adding liquids.",
        "reveal_policy": "You may coach safe physical assembly and systematic troubleshooting. Do not encourage unapproved material changes."
      },
      {
        "id": "conductive-apply-completion",
        "type": "completion_gate",
        "incomplete_title": "Restore one safe light",
        "complete_title": "First Glow City light restored",
        "incomplete_prompt": "Complete the kit, safety, and first-light evidence.",
        "complete_prompt": "You restored a physical light. Next, Test faults, add protective structure, and improve a full landmark.",
        "rules": [
          {
            "field": "physical_kit_inventory",
            "condition": "min_items",
            "value": 7
          },
          {
            "field": "physical_safety_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "first_light_build_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "first_light_test",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "conductive-dough-test-improve": {
    "step_id": "conductive-dough-test-improve",
    "title": "Catch the Fault and Rebuild Glow City",
    "subtitle": "Diagnose, Protect, Design, and Redesign",
    "phase": "Test & Improve",
    "story": "Diagnose the shortcut thief, use insulating dough to keep conductive paths separated, design a landmark, then change one variable and compare evidence from two trials.",
    "self_guided": {
      "title": "Improve the Glow City landmark",
      "say": "A failure can be useful evidence. Keep the battery holder switched off before every adjustment, and change only one planned variable between trials.",
      "do_now": "Investigate the bypass clues, plan safe insulating separation, design your landmark, and compare two documented trials. Turn the power off before changes and ask Professor Swan what evidence to check next. Stop and ask an adult for damaged or hot components.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You diagnosed shortcut clues, checked insulating separation, planned a Glow City landmark, and recorded two trials with one deliberate design change. Now there is evidence to compare.",
      "next_preview": "Next, explain the circuit from power source to LED using your own test records, including anything that did not work."
    },
    "self_guided_block_guidance": {
      "shortcut-prediction": {
        "title": "Investigate a possible bypass",
        "try_now": "Predict what might happen when two conductive paths touch, using only the virtual evidence."
      },
      "landmark-first-trial": {
        "title": "Record the first evidence",
        "try_now": "After completing the circuit checklist, test the kit briefly and record what actually happened. Ask Professor Swan if the result is unexpected."
      },
      "redesign-variable": {
        "title": "Change only one thing",
        "try_now": "Use Trial 1 evidence to choose a single safe redesign variable."
      }
    },
    "blocks": [
      {
        "id": "step6-story",
        "type": "story_hook",
        "eyebrow": "CLUE 4 — STOP THE SHORTCUT THIEF",
        "title": "The current found a bypass",
        "body": "Current can divide among available paths. If the conductive dough pieces touch, a low-resistance bypass may carry enough current around the LED that the light goes dark.",
        "tone": "mystery"
      },
      {
        "id": "shortcut-prediction",
        "type": "choice_cards",
        "title": "Predict the touching-path result",
        "prompt": "What is most likely if the positive and return dough paths touch directly?",
        "choices": [
          {
            "id": "brighter",
            "label": "The LED becomes brighter because there is more connection"
          },
          {
            "id": "bypass",
            "label": "Much more current may use the low-resistance bypass around the LED"
          },
          {
            "id": "no_change",
            "label": "Nothing changes"
          },
          {
            "id": "reverse",
            "label": "The LED automatically reverses direction"
          },
          {
            "id": "need_model",
            "label": "I need to trace both possible paths"
          }
        ],
        "save_as": "shortcut_prediction",
        "required": true
      },
      {
        "id": "shortcut-gallery",
        "type": "image_gallery",
        "title": "Trace the competing routes",
        "prompt": "Identify which layout forces current through the LED and which creates a bypass.",
        "save_as": "shortcut_image_examined",
        "images": [
          {
            "id": "shortcut",
            "label": "Working path versus bypass",
            "path": "conductive-dough/step-6/shortcut_thief.png",
            "caption": "A direct conductive connection can form a lower-resistance route around the LED.",
            "alt": "Diagram comparing a working conductive dough LED path and a short-circuit bypass."
          }
        ]
      },
      {
        "id": "short-video",
        "type": "video_asset",
        "title": "Optional evidence video: Diagnosing Short Circuits",
        "mission": "Use this video as extra evidence. Translate “easy path” into the more accurate idea of current dividing, with much more through a lower-resistance bypass.",
        "save_as": "short_video_opened",
        "asset": {
          "id": "short-circuit-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7018,
            "tag_id": 30035
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "fault-classification",
        "type": "classification",
        "title": "Classify the Glow City faults",
        "prompt": "Choose the strongest diagnosis for each observation.",
        "categories": [
          {
            "id": "short",
            "label": "Low-resistance bypass / short circuit"
          },
          {
            "id": "open",
            "label": "Open or loose connection"
          },
          {
            "id": "polarity",
            "label": "LED polarity problem"
          },
          {
            "id": "power",
            "label": "Battery/power problem"
          }
        ],
        "items": [
          {
            "id": "touching_dough",
            "label": "The LED was working, then went dark when the two conductive pieces touched"
          },
          {
            "id": "loose_lead",
            "label": "The LED flickers when one connecting lead moves"
          },
          {
            "id": "reversed_led",
            "label": "The path is complete, but the LED lights after its legs are swapped"
          },
          {
            "id": "switch_off",
            "label": "Every component is connected, but the battery-holder switch is OFF"
          }
        ],
        "save_as": "fault_diagnoses",
        "required": true
      },
      {
        "id": "shortcut-sequence",
        "type": "sequence_builder",
        "title": "Build the cause-and-effect chain",
        "prompt": "Arrange what happens when the conductive paths touch.",
        "save_as": "shortcut_causal_chain",
        "items": [
          {
            "id": "touch",
            "label": "The two conductive dough paths touch"
          },
          {
            "id": "bypass_forms",
            "label": "A low-resistance bypass forms around the LED"
          },
          {
            "id": "current_divides",
            "label": "Current divides, with much more through the bypass"
          },
          {
            "id": "led_current_low",
            "label": "Too little current passes through the LED to glow normally"
          },
          {
            "id": "separate_fix",
            "label": "Power is switched off and the paths are separated"
          }
        ],
        "correct_order": [
          "touch",
          "bypass_forms",
          "current_divides",
          "led_current_low",
          "separate_fix"
        ],
        "correct_feedback": "Correct. You described the shortcut without claiming that current can use only one path.",
        "retry_feedback": "Begin with the physical contact and end with a power-off repair."
      },
      {
        "id": "safe-short-fix",
        "type": "evidence_response",
        "title": "Write the safe repair plan",
        "prompt": "How would you fix a suspected short circuit without adding materials or touching the powered circuit?",
        "placeholder": "First I would switch off... Then I would inspect...",
        "save_as": "safe_short_fix",
        "min_length": 30,
        "required": true
      },
      {
        "id": "step6-saras",
        "type": "saras_checkpoint",
        "title": "Interrogate the shortcut thief with Saras",
        "suggested_questions": [
          "Can current travel through more than one path?",
          "Why can a low-resistance bypass make the LED dark?",
          "How is a short circuit different from an open circuit?",
          "What is the safest order for fixing touching dough paths?"
        ],
        "hidden_context": "The student is diagnosing short circuits in a low-voltage conductive dough kit. Use accurate language: current can divide among paths; a direct conductive contact can create a much lower-resistance bypass and divert enough current around the LED. Do not repeat “electricity is lazy” or “all current always takes one path.” Keep physical direct-short demonstrations teacher-controlled, momentary, and optional. Always switch off before repair.",
        "reveal_policy": "The short-circuit mechanism may be fully explained. Do not advise prolonged or unsupervised direct shorts."
      },
      {
        "id": "step7-story",
        "type": "story_hook",
        "eyebrow": "CLUE 2 — BUILD THE WALLS",
        "title": "A wall controls where current must not go",
        "body": "Insulating dough is not decoration. It provides structure and high resistance between conductive sections.",
        "tone": "mystery"
      },
      {
        "id": "wall-placement",
        "type": "choice_cards",
        "title": "Where should the insulating dough go?",
        "prompt": "Choose the placement that best prevents accidental conductive contact.",
        "choices": [
          {
            "id": "between_paths",
            "label": "Between conductive sections that must remain electrically separate"
          },
          {
            "id": "over_battery",
            "label": "Covering the battery terminals"
          },
          {
            "id": "replace_led",
            "label": "In place of the LED"
          },
          {
            "id": "mixed_everywhere",
            "label": "Mixed completely into all conductive dough"
          },
          {
            "id": "need_plan",
            "label": "I need to mark the positive and return paths first"
          }
        ],
        "save_as": "wall_placement_prediction",
        "required": true
      },
      {
        "id": "wall-gallery",
        "type": "image_gallery",
        "title": "Inspect the structural clue",
        "prompt": "Compare the insulating-wall model with alternate sculpture shapes.",
        "save_as": "wall_image_examined",
        "images": [
          {
            "id": "wall",
            "label": "Insulating wall model",
            "path": "conductive-dough/step-7/insulating_wall.png",
            "caption": "The white insulating layer separates the conductive positive and return paths.",
            "alt": "Diagram of an insulating dough wall between two conductive paths."
          },
          {
            "id": "shapes",
            "label": "PWO alternate shapes",
            "path": "conductive-dough/step-7/explore_more_shapes.png",
            "caption": "Different shapes can still work if the electrical paths remain correctly separated.",
            "alt": "Conductive Dough PWO Explore More scene with alternate shapes."
          }
        ]
      },
      {
        "id": "exploremore-pwo",
        "type": "pwo_asset",
        "title": "Explore alternate dough shapes",
        "mission": "Open Explore More. Compare shapes and ask where an insulating barrier would prevent hidden contact.",
        "save_as": "exploremore_pwo_opened",
        "asset": {
          "id": "conductive-exploremore",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1170,
            "scene_name": "exploremore"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 170,
            "collection_item_id": 152
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 937,
            "source_topic_id": 962
          }
        }
      },
      {
        "id": "wall-jobs",
        "type": "matching",
        "title": "Assign the insulating wall its jobs",
        "prompt": "Choose the strongest role for each design need.",
        "save_as": "wall_jobs",
        "items": [
          {
            "id": "separation",
            "label": "Keep positive and return paths apart",
            "options": [
              {
                "id": "insulator",
                "label": "Use insulating dough as a high-resistance barrier"
              },
              {
                "id": "battery",
                "label": "Add more batteries"
              },
              {
                "id": "reverse_led",
                "label": "Reverse every LED"
              }
            ]
          },
          {
            "id": "structure",
            "label": "Support a layered sculpture",
            "options": [
              {
                "id": "insulator_structure",
                "label": "Shape insulating dough as structural material between paths"
              },
              {
                "id": "touch_paths",
                "label": "Press conductive paths together"
              },
              {
                "id": "remove_barrier",
                "label": "Remove the insulating barrier"
              }
            ]
          },
          {
            "id": "hidden_contact",
            "label": "Prevent contact around the side of a barrier",
            "options": [
              {
                "id": "inspect_edges",
                "label": "Extend and inspect the barrier around edges"
              },
              {
                "id": "ignore",
                "label": "Assume hidden edges cannot touch"
              },
              {
                "id": "more_power",
                "label": "Use more batteries"
              }
            ]
          }
        ],
        "required": true
      },
      {
        "id": "wall-build-check",
        "type": "build_checklist",
        "title": "Add a protective insulating wall",
        "prompt": "Switch power OFF before changing the physical sculpture.",
        "save_as": "wall_build_check",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "power_off",
            "label": "Battery-holder switch is OFF before adjustment"
          },
          {
            "id": "paths_marked",
            "label": "Positive and return conductive paths are identified"
          },
          {
            "id": "barrier_inserted",
            "label": "Insulating dough is placed between paths without covering required contacts"
          },
          {
            "id": "edges_checked",
            "label": "Edges and underside are checked for hidden conductive contact"
          },
          {
            "id": "brief_retest",
            "label": "Circuit is briefly retested, observed, and switched OFF"
          }
        ]
      },
      {
        "id": "wall-comparison",
        "type": "experiment_record",
        "title": "Compare before and after the wall",
        "prompt": "Record whether the barrier improved separation and reliability.",
        "save_as": "wall_comparison_record",
        "fields": [
          {
            "id": "before",
            "label": "Before the wall",
            "type": "choice",
            "choices": [
              {
                "id": "stable",
                "label": "Paths stayed separated"
              },
              {
                "id": "touching",
                "label": "Paths touched or nearly touched"
              },
              {
                "id": "dark",
                "label": "LED was dark"
              },
              {
                "id": "flicker",
                "label": "LED flickered"
              }
            ]
          },
          {
            "id": "after",
            "label": "After the wall",
            "type": "choice",
            "choices": [
              {
                "id": "stable_lit",
                "label": "Paths stayed separated and LED lit"
              },
              {
                "id": "still_touching",
                "label": "Paths still touched"
              },
              {
                "id": "still_dark",
                "label": "LED stayed dark"
              },
              {
                "id": "need_test",
                "label": "Need another controlled test"
              }
            ]
          },
          {
            "id": "evidence",
            "label": "Evidence that the wall helped or did not help",
            "type": "text",
            "placeholder": "The barrier changed...",
            "min_length": 20
          }
        ]
      },
      {
        "id": "step7-saras",
        "type": "saras_checkpoint",
        "title": "Review the protective wall with Saras",
        "suggested_questions": [
          "Why is insulation part of the structure, not just the circuit?",
          "How can hidden edge contact create a shortcut?",
          "What evidence shows the wall improved reliability?",
          "How can I redesign the wall without changing the whole sculpture?"
        ],
        "hidden_context": "The student is using insulating dough as both electrical separation and structure. Explain high resistance rather than an absolute magical block. Help inspect edges, undersides, and sagging paths. Use the student’s before/after record. Do not suggest adding water or using extra batteries.",
        "reveal_policy": "Insulation and structural separation may be fully discussed. Encourage evidence and one-variable changes."
      },
      {
        "id": "step8-story",
        "type": "story_hook",
        "eyebrow": "DESIGN COMMISSION",
        "title": "Make the sculpture beautiful because the circuit is reliable",
        "body": "The outside shape is your choice. The inside rules are not: separated conductive paths, correctly oriented LEDs, strong contacts, and insulating structure where needed.",
        "tone": "mystery"
      },
      {
        "id": "landmark-gallery",
        "type": "image_gallery",
        "title": "Gather design inspiration",
        "prompt": "Use the glowing creature for imagination and the blueprint for circuit planning.",
        "save_as": "landmark_inspiration",
        "images": [
          {
            "id": "creature",
            "label": "Glowing creature inspiration",
            "path": "conductive-dough/step-8/glowing_creature_inspiration.jpg",
            "caption": "A creative form can hide a carefully planned circuit.",
            "alt": "A glowing dough creature."
          },
          {
            "id": "blueprint",
            "label": "Electrical blueprint",
            "path": "conductive-dough/step-8/glow_city_blueprint.png",
            "caption": "Plan conductive paths, insulating structure, LED bridges, and accessible battery connections before sculpting details.",
            "alt": "Blueprint for a Glow City conductive dough landmark."
          },
          {
            "id": "kit",
            "label": "Available design parts",
            "path": "conductive-dough/step-8/kit_design_parts.png",
            "caption": "Use the supplied dough, LEDs, leads, and four-AA battery holder. The buzzer is optional and teacher-approved only.",
            "alt": "Labeled Conductive Dough kit parts."
          }
        ]
      },
      {
        "id": "landmark-type",
        "type": "choice_cards",
        "title": "Choose your Glow City landmark",
        "prompt": "The category is flexible; the circuit criteria remain fixed.",
        "choices": [
          {
            "id": "creature",
            "label": "Glowing creature or animal"
          },
          {
            "id": "tower",
            "label": "Festival tower or building"
          },
          {
            "id": "garden",
            "label": "Light garden or flower"
          },
          {
            "id": "vehicle",
            "label": "Glow City vehicle or robot"
          },
          {
            "id": "original",
            "label": "An original landmark of my own"
          }
        ],
        "save_as": "landmark_type",
        "required": true
      },
      {
        "id": "criteria-constraints",
        "type": "classification",
        "title": "Engineer the design brief",
        "prompt": "Classify each statement as a success criterion or a constraint.",
        "categories": [
          {
            "id": "criterion",
            "label": "Success criterion"
          },
          {
            "id": "constraint",
            "label": "Constraint"
          }
        ],
        "items": [
          {
            "id": "lights",
            "label": "At least one LED must glow reliably"
          },
          {
            "id": "separate",
            "label": "Conductive paths must remain separated except through intended components"
          },
          {
            "id": "evidence",
            "label": "The team must explain the design using test evidence"
          },
          {
            "id": "kit_only",
            "label": "Use only teacher-approved kit materials"
          },
          {
            "id": "four_aa",
            "label": "Use the supplied four-AA battery holder only"
          },
          {
            "id": "time",
            "label": "Complete the landmark during the available camp session"
          },
          {
            "id": "safety",
            "label": "Power must be off during adjustments"
          }
        ],
        "save_as": "landmark_criteria_constraints",
        "required": true
      },
      {
        "id": "design-plan",
        "type": "experiment_record",
        "title": "Write the landmark blueprint",
        "prompt": "Plan the electrical system before sculpting. A drawing upload is not required for this renderer; a clear written blueprint is enough.",
        "save_as": "landmark_design_plan",
        "fields": [
          {
            "id": "goal",
            "label": "What should the landmark do?",
            "type": "text",
            "placeholder": "Our landmark will...",
            "min_length": 15
          },
          {
            "id": "path_plan",
            "label": "Where will the positive and return conductive paths go?",
            "type": "text",
            "placeholder": "The two paths will...",
            "min_length": 25
          },
          {
            "id": "insulation_plan",
            "label": "Where will insulating dough prevent contact?",
            "type": "text",
            "placeholder": "The insulating wall will...",
            "min_length": 20
          },
          {
            "id": "led_plan",
            "label": "Where and how will the LED(s) bridge the paths?",
            "type": "text",
            "placeholder": "The long leg will...",
            "min_length": 20
          },
          {
            "id": "weak_point",
            "label": "What is the most likely failure point?",
            "type": "text",
            "placeholder": "The place most likely to fail is... because...",
            "min_length": 20
          }
        ]
      },
      {
        "id": "landmark-build",
        "type": "build_checklist",
        "title": "Build the Glow City landmark",
        "prompt": "Use the plan, keep power OFF during adjustments, and test in short intervals.",
        "save_as": "landmark_build_check",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "paths_built",
            "label": "Two conductive paths were built according to the plan"
          },
          {
            "id": "insulation_added",
            "label": "Insulating dough was added where paths could touch"
          },
          {
            "id": "leds_oriented",
            "label": "LED polarity was checked before power"
          },
          {
            "id": "contacts_secure",
            "label": "Lead and LED contacts are firm but not forced"
          },
          {
            "id": "brief_test",
            "label": "Landmark was tested briefly and switched OFF"
          },
          {
            "id": "buzzer_optional",
            "label": "Optional buzzer was used only with teacher approval—or intentionally skipped"
          }
        ]
      },
      {
        "id": "landmark-first-trial",
        "type": "experiment_record",
        "title": "Record Landmark Trial 1",
        "prompt": "Record the first full landmark test before redesigning.",
        "save_as": "landmark_trial_one",
        "fields": [
          {
            "id": "light_result",
            "label": "Light result",
            "type": "choice",
            "choices": [
              {
                "id": "all_lit",
                "label": "All intended LEDs lit"
              },
              {
                "id": "some_lit",
                "label": "Some LEDs lit"
              },
              {
                "id": "dark",
                "label": "No LEDs lit"
              },
              {
                "id": "flicker",
                "label": "Lights flickered"
              }
            ]
          },
          {
            "id": "structure",
            "label": "Structure result",
            "type": "choice",
            "choices": [
              {
                "id": "stable",
                "label": "Stayed stable and separated"
              },
              {
                "id": "sagged",
                "label": "Sagged or paths moved"
              },
              {
                "id": "touching",
                "label": "Conductive paths touched"
              },
              {
                "id": "other",
                "label": "Another issue"
              }
            ]
          },
          {
            "id": "evidence",
            "label": "Most important observation",
            "type": "text",
            "placeholder": "During Trial 1...",
            "min_length": 20
          }
        ]
      },
      {
        "id": "step8-saras",
        "type": "saras_checkpoint",
        "title": "Hold a design review with Saras",
        "suggested_questions": [
          "Where is my design most likely to short circuit?",
          "Do my LEDs clearly bridge two separate paths?",
          "How can I make the structure stable without hiding required contacts?",
          "What should I record during the first full test?"
        ],
        "hidden_context": "The student is designing a creative conductive dough landmark using the actual four-AA kit. Use criteria, constraints, the written blueprint, and Trial 1 evidence. The buzzer is optional and only teacher-approved; do not require a motor. Challenge the design by locating possible contact, polarity, stability, and connection failures. Do not author the plan for the student.",
        "reveal_policy": "All core circuit concepts may be used. Coach design reasoning and evidence collection."
      },
      {
        "id": "step9-story",
        "type": "story_hook",
        "eyebrow": "RELIABILITY TEST",
        "title": "A failure is evidence, not defeat",
        "body": "Use a fixed diagnostic order: power, separation, polarity, contact, then one controlled change.",
        "tone": "mystery"
      },
      {
        "id": "troubleshooting-image",
        "type": "image_gallery",
        "title": "Use the Glow City diagnostic order",
        "prompt": "Do not skip to random rebuilding.",
        "save_as": "troubleshooting_image_examined",
        "images": [
          {
            "id": "flow",
            "label": "Troubleshooting flow",
            "path": "conductive-dough/step-9/troubleshooting_flow.png",
            "caption": "Check one cause at a time, then retest after one deliberate change.",
            "alt": "Five-step conductive dough circuit troubleshooting flow."
          }
        ]
      },
      {
        "id": "trial1-summary",
        "type": "prior_work_summary",
        "title": "Review Trial 1 evidence",
        "prompt": "Use the actual record rather than memory.",
        "items": [
          {
            "source_step_id": "conductive-dough-test-improve",
            "field": "landmark_type",
            "label": "Landmark type",
            "value_labels": {
              "creature": "Glowing creature or animal",
              "tower": "Festival tower or building",
              "garden": "Light garden or flower",
              "vehicle": "Glow City vehicle or robot",
              "original": "Original landmark"
            }
          },
          {
            "source_step_id": "conductive-dough-test-improve",
            "field": "landmark_trial_one",
            "label": "Trial 1 record",
            "field_labels": {
              "light_result": "Lights",
              "structure": "Structure",
              "evidence": "Observation"
            },
            "value_labels": {
              "all_lit": "All intended LEDs lit",
              "some_lit": "Some LEDs lit",
              "dark": "No LEDs lit",
              "flicker": "Lights flickered",
              "stable": "Stable and separated",
              "sagged": "Sagged or paths moved",
              "touching": "Conductive paths touched",
              "other": "Another issue"
            }
          },
          {
            "source_step_id": "conductive-dough-test-improve",
            "field": "landmark_design_plan.weak_point",
            "label": "Predicted weak point"
          }
        ]
      },
      {
        "id": "fault-hypothesis",
        "type": "classification",
        "title": "Diagnose the evidence",
        "prompt": "Assign each clue to the most likely fault family.",
        "categories": [
          {
            "id": "power",
            "label": "Power/battery"
          },
          {
            "id": "open",
            "label": "Loose/open contact"
          },
          {
            "id": "polarity",
            "label": "LED polarity"
          },
          {
            "id": "short",
            "label": "Touching paths / bypass"
          },
          {
            "id": "structure",
            "label": "Insulation/structure"
          }
        ],
        "items": [
          {
            "id": "all_dark_switch",
            "label": "All lights are dark and the holder switch is OFF"
          },
          {
            "id": "flicker_leg",
            "label": "One LED flickers when its leg moves"
          },
          {
            "id": "reversed_one",
            "label": "One LED remains dark but lights after being reversed"
          },
          {
            "id": "pressed_touch",
            "label": "The landmark goes dark only when two colored sections touch"
          },
          {
            "id": "wall_gap",
            "label": "A white insulating wall ends before the edge, allowing hidden contact"
          }
        ],
        "save_as": "redesign_fault_classification",
        "required": true
      },
      {
        "id": "redesign-variable",
        "type": "choice_cards",
        "title": "Choose one variable to change",
        "prompt": "Choose the change that best addresses your evidence. Keep other important variables as consistent as possible.",
        "choices": [
          {
            "id": "battery_switch",
            "label": "Correct the battery/switch condition"
          },
          {
            "id": "secure_contact",
            "label": "Improve one loose lead or LED contact"
          },
          {
            "id": "reverse_led",
            "label": "Reverse one suspected LED"
          },
          {
            "id": "separate_paths",
            "label": "Separate one touching conductive area"
          },
          {
            "id": "extend_insulation",
            "label": "Extend or reshape one insulating barrier"
          }
        ],
        "save_as": "redesign_variable",
        "required": true
      },
      {
        "id": "redesign-prediction",
        "type": "evidence_response",
        "title": "Predict the effect before changing it",
        "prompt": "What should improve if your diagnosis is correct?",
        "placeholder": "If we change... then... because...",
        "save_as": "redesign_prediction",
        "min_length": 25,
        "required": true
      },
      {
        "id": "trial2-record",
        "type": "experiment_record",
        "title": "Record Landmark Trial 2",
        "prompt": "Switch OFF, make the one change, then retest under the same conditions.",
        "save_as": "landmark_trial_two",
        "fields": [
          {
            "id": "light_result",
            "label": "Light result",
            "type": "choice",
            "choices": [
              {
                "id": "all_lit",
                "label": "All intended LEDs lit"
              },
              {
                "id": "some_lit",
                "label": "Some LEDs lit"
              },
              {
                "id": "dark",
                "label": "No LEDs lit"
              },
              {
                "id": "flicker",
                "label": "Lights flickered"
              }
            ]
          },
          {
            "id": "structure",
            "label": "Structure result",
            "type": "choice",
            "choices": [
              {
                "id": "stable",
                "label": "Stayed stable and separated"
              },
              {
                "id": "sagged",
                "label": "Sagged or paths moved"
              },
              {
                "id": "touching",
                "label": "Conductive paths touched"
              },
              {
                "id": "other",
                "label": "Another issue"
              }
            ]
          },
          {
            "id": "same_conditions",
            "label": "Were the other important test conditions kept similar?",
            "type": "choice",
            "choices": [
              {
                "id": "yes",
                "label": "Yes, as closely as practical"
              },
              {
                "id": "partly",
                "label": "Partly"
              },
              {
                "id": "no",
                "label": "No"
              }
            ]
          },
          {
            "id": "evidence",
            "label": "Most important Trial 2 observation",
            "type": "text",
            "placeholder": "During Trial 2...",
            "min_length": 20
          }
        ]
      },
      {
        "id": "redesign-conclusion",
        "type": "evidence_response",
        "title": "Judge the redesign using evidence",
        "prompt": "Did the one change improve reliability, make no clear difference, or create a new problem? Compare the trials.",
        "placeholder": "Trial 1 showed... Trial 2 showed... Therefore...",
        "save_as": "redesign_conclusion",
        "min_length": 40,
        "required": true
      },
      {
        "id": "step9-saras",
        "type": "saras_checkpoint",
        "title": "Analyze the redesign with Saras",
        "suggested_questions": [
          "Which diagnosis best matches my Trial 1 evidence?",
          "Did I really change only one variable?",
          "What evidence would count as improved reliability?",
          "Can a redesign be useful even if Trial 2 is worse?"
        ],
        "hidden_context": "The student is troubleshooting a Conductive Dough landmark using a one-variable redesign. Use prior Trial 1, the selected variable, prediction, Trial 2, and conclusion. Negative or mixed results are valid evidence. Ask the learner to compare like conditions and distinguish claims from observations. Do not invent measurements or declare improvement without evidence.",
        "reveal_policy": "All science may be discussed. Preserve the student’s ownership of the conclusion."
      },
      {
        "id": "conductive-test-improve-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the Glow City evidence cycle",
        "complete_title": "Landmark tested and improved",
        "incomplete_prompt": "Finish the fault diagnosis, insulating-wall comparison, first landmark trial, and one-variable redesign.",
        "complete_prompt": "You now have evidence from the repaired and redesigned landmark. Next, Explain and defend the Glow City rescue.",
        "rules": [
          {
            "field": "shortcut_prediction",
            "condition": "answered"
          },
          {
            "field": "fault_diagnoses",
            "condition": "completed"
          },
          {
            "field": "shortcut_causal_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "safe_short_fix",
            "condition": "min_length",
            "value": 30
          },
          {
            "field": "wall_placement_prediction",
            "condition": "answered"
          },
          {
            "field": "exploremore_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "wall_jobs",
            "condition": "completed"
          },
          {
            "field": "wall_build_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "wall_comparison_record",
            "condition": "record_complete"
          },
          {
            "field": "landmark_type",
            "condition": "answered"
          },
          {
            "field": "landmark_criteria_constraints",
            "condition": "completed"
          },
          {
            "field": "landmark_design_plan",
            "condition": "record_complete"
          },
          {
            "field": "landmark_build_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "landmark_trial_one",
            "condition": "record_complete"
          },
          {
            "field": "redesign_fault_classification",
            "condition": "completed"
          },
          {
            "field": "redesign_variable",
            "condition": "answered"
          },
          {
            "field": "redesign_prediction",
            "condition": "min_length",
            "value": 25
          },
          {
            "field": "landmark_trial_two",
            "condition": "record_complete"
          },
          {
            "field": "redesign_conclusion",
            "condition": "min_length",
            "value": 40
          }
        ]
      }
    ]
  },
  "conductive-dough-explain": {
    "step_id": "conductive-dough-explain",
    "title": "Defend the Glow City Rescue",
    "subtitle": "Circuit Sculptor Rescue Report",
    "phase": "Explain",
    "story": "Use the complete circuit model, polarity evidence, short-circuit explanation, and redesign trials to defend why the landmark works and what your evidence supports.",
    "self_guided": {
      "title": "Defend your electrical rescue",
      "say": "A light turning on is a result, not a complete explanation. Link your observed path, polarity, insulation, and any fault evidence.",
      "do_now": "Review your results, complete the causal-chain and honesty checks, and write your own Circuit Sculptor report.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You arranged the final circuit explanation, checked an important shortcut misconception, and completed your rescue report using your recorded evidence.",
      "next_preview": "Next, look back at your first blackout theory and explain which evidence changed—or strengthened—it."
    },
    "self_guided_block_guidance": {
      "rescue-report": {
        "title": "Defend Glow City",
        "try_now": "Connect the reported result to circuit path, polarity, insulation, and trial evidence."
      }
    },
    "blocks": [
      {
        "id": "step10-story",
        "type": "story_hook",
        "eyebrow": "FESTIVAL AUTHORIZATION",
        "title": "A glowing sculpture is not enough—explain the system",
        "body": "Your report must trace the circuit, explain polarity and insulation, compare both trials, and state what remains uncertain.",
        "tone": "mystery"
      },
      {
        "id": "final-gallery",
        "type": "image_gallery",
        "title": "Prepare the final Glow City report",
        "prompt": "Use the causal chain and restored landmark as presentation guides.",
        "save_as": "final_report_image",
        "images": [
          {
            "id": "chain",
            "label": "Final circuit chain",
            "path": "conductive-dough/step-10/final_glow_city_chain.png",
            "caption": "The battery supplies energy; a complete path carries current through conductive dough and correctly oriented components; insulation prevents unwanted contact.",
            "alt": "Final Conductive Dough circuit chain diagram."
          },
          {
            "id": "restored",
            "label": "Glow City restored",
            "path": "conductive-dough/step-10/glow_city_restored.png",
            "caption": "A successful landmark combines creative structure with an intentionally planned circuit.",
            "alt": "Glowing dough creature and final report checklist."
          }
        ]
      },
      {
        "id": "prior-evidence",
        "type": "prior_work_summary",
        "title": "Bring forward the rescue evidence",
        "prompt": "Use your recorded work in the final explanation.",
        "items": [
          {
            "source_step_id": "conductive-dough-wonder",
            "field": "opening_cause_prediction",
            "label": "Opening theory",
            "value_labels": {
              "battery": "Energy source problem",
              "broken_path": "Incomplete path",
              "wrong_direction": "LED direction",
              "touching_paths": "Touching paths/shortcut",
              "material": "Material conductivity",
              "need_evidence": "Several causes possible"
            }
          },
          {
            "source_step_id": "conductive-dough-apply",
            "field": "first_light_test",
            "label": "First physical light test",
            "field_labels": {
              "result": "Result",
              "paths": "Path separation",
              "observation": "Observation"
            },
            "value_labels": {
              "bright": "Glowed clearly",
              "dim": "Glowed dimly",
              "dark": "Stayed dark",
              "flicker": "Flickered",
              "yes": "Separated",
              "touching": "Touched",
              "unsure": "Unsure"
            }
          },
          {
            "source_step_id": "conductive-dough-test-improve",
            "field": "redesign_variable",
            "label": "One redesign variable",
            "value_labels": {
              "battery_switch": "Battery/switch condition",
              "secure_contact": "Secure a contact",
              "reverse_led": "Reverse an LED",
              "separate_paths": "Separate touching paths",
              "extend_insulation": "Extend insulating barrier"
            }
          },
          {
            "source_step_id": "conductive-dough-test-improve",
            "field": "redesign_conclusion",
            "label": "Trial comparison conclusion"
          }
        ]
      },
      {
        "id": "final-chain",
        "type": "sequence_builder",
        "title": "Build the final causal chain",
        "prompt": "Arrange the circuit events from energy source to visible light.",
        "save_as": "final_circuit_chain",
        "items": [
          {
            "id": "chemical",
            "label": "Chemical energy is stored in four AA batteries"
          },
          {
            "id": "closed_path",
            "label": "The switch and connections form a complete circuit"
          },
          {
            "id": "conductive_path",
            "label": "Current passes through the conductive dough path"
          },
          {
            "id": "led_direction",
            "label": "A correctly oriented LED allows current through and emits light"
          },
          {
            "id": "return",
            "label": "Current continues through the return path back to the battery"
          },
          {
            "id": "insulation",
            "label": "Insulating dough prevents unwanted conductive contact"
          }
        ],
        "correct_order": [
          "chemical",
          "closed_path",
          "conductive_path",
          "led_direction",
          "return",
          "insulation"
        ],
        "correct_feedback": "Excellent. You traced the working path and included the protective role of insulation.",
        "retry_feedback": "Place the energy source first, trace the complete path, then add the protection rule."
      },
      {
        "id": "honesty-check",
        "type": "quiz_question",
        "title": "Scientific honesty check",
        "prompt": "Which statement best explains a short-circuit bypass in this activity?",
        "choices": [
          {
            "id": "divide_low_resistance",
            "label": "Current can divide among paths; much more may flow through a lower-resistance bypass around the LED"
          },
          {
            "id": "lazy_only",
            "label": "Electricity is lazy and can use only one path"
          },
          {
            "id": "energy_disappears",
            "label": "The battery’s energy disappears when dough touches"
          },
          {
            "id": "squeeze_together",
            "label": "Pressing every conductive piece together always repairs the circuit"
          }
        ],
        "correct_answer": "divide_low_resistance",
        "save_as": "final_honesty_check",
        "correct_feedback": "Yes. This explains the bypass without pretending that current can only use one path.",
        "incorrect_feedback": "Choose the explanation that allows current to divide and avoids unreliable material claims.",
        "self_guided_incorrect_feedback": "Trace each path in the diagram and compare your choice with the observed light behavior. What evidence supports your explanation?"
      },
      {
        "id": "rescue-report",
        "type": "experiment_record",
        "title": "Write the Circuit Sculptor Rescue Report",
        "prompt": "Use complete sentences and evidence from both trials.",
        "save_as": "circuit_sculptor_report",
        "fields": [
          {
            "id": "roads",
            "label": "1. What are the electrical roads and why do they work?",
            "type": "text",
            "placeholder": "The conductive dough works because...",
            "min_length": 30
          },
          {
            "id": "walls",
            "label": "2. What do the insulating walls do?",
            "type": "text",
            "placeholder": "The insulating dough...",
            "min_length": 25
          },
          {
            "id": "gate",
            "label": "3. Why is the LED a one-way light gate?",
            "type": "text",
            "placeholder": "The LED must be oriented...",
            "min_length": 25
          },
          {
            "id": "shortcut",
            "label": "4. How does the shortcut thief make a light go dark?",
            "type": "text",
            "placeholder": "If conductive paths touch...",
            "min_length": 30
          },
          {
            "id": "evidence",
            "label": "5. Which two observations best support your redesign claim?",
            "type": "text",
            "placeholder": "Trial 1 showed... Trial 2 showed...",
            "min_length": 40
          }
        ]
      },
      {
        "id": "step10-saras",
        "type": "saras_checkpoint",
        "title": "Rehearse the Glow City report with Saras",
        "suggested_questions": [
          "Ask me one rescue-report question at a time.",
          "Does my explanation trace a complete circuit?",
          "Which evidence best supports my redesign claim?",
          "Help me remove inaccurate “lazy electricity” wording without rewriting my answer."
        ],
        "hidden_context": "The student is preparing the final report for The Great Glow City Blackout. Use final_circuit_chain, final_honesty_check, circuit_sculptor_report, final_reflection, and prior trial evidence. The accurate core: four AA batteries supply energy; a closed path allows current through conductive dough; LED polarity matters; insulating dough has high resistance and separates paths; a low-resistance bypass can divert current around an LED. Do not recommend adding liquids or require a motor. Coach one question at a time and preserve the student’s wording.",
        "reveal_policy": "The complete science and all prior evidence may be discussed. Coach, do not author the final report."
      },
      {
        "id": "conductive-explain-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish the Glow City rescue report",
        "complete_title": "Rescue explanation complete",
        "incomplete_prompt": "Complete the causal chain, scientific honesty check, and evidence-based report.",
        "complete_prompt": "Your explanation is complete. Next, Reflect on how your thinking changed.",
        "rules": [
          {
            "field": "final_circuit_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "final_honesty_check",
            "condition": "equals",
            "value": "divide_low_resistance"
          },
          {
            "field": "circuit_sculptor_report",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "conductive-dough-reflect": {
    "step_id": "conductive-dough-reflect",
    "title": "What Changed Your Thinking?",
    "subtitle": "From Blackout Theory to Circuit Evidence",
    "phase": "Reflect",
    "story": "Look back at the blackout theory you started with and explain how the tests changed your understanding of current paths, polarity, insulation, and redesign.",
    "self_guided": {
      "title": "What changed your mind?",
      "say": "Good investigators are willing to revise their theories. Compare what you first believed to what you can now support.",
      "do_now": "Use the Glow City case file and test records to write one change in your thinking and what caused it.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You wrote a reflection about your original idea and the evidence you considered. A scientist can keep a theory, revise it, or reject it—but should explain why.",
      "next_preview": "Next, connect the Glow City circuit rules to a new design question or challenge."
    },
    "self_guided_block_guidance": {
      "final-reflection": {
        "title": "Revisit your first idea",
        "try_now": "Which clue changed your explanation of the blackout?"
      }
    },
    "blocks": [
      {
        "id": "conductive-reflect-story",
        "type": "story_hook",
        "eyebrow": "REFLECT",
        "title": "The best rescue explanation grew from evidence",
        "body": "A useful theory can change when tests reveal a better explanation. Use your own Glow City evidence to describe that change.",
        "tone": "mystery"
      },
      {
        "id": "final-reflection",
        "type": "evidence_response",
        "title": "Reflect on your Circuit Sculptor journey",
        "prompt": "What changed from your opening theory? What are you proud of, and what would you investigate next?",
        "placeholder": "At first I thought... Now I know... I am proud that... Next I would test...",
        "save_as": "final_reflection",
        "min_length": 45,
        "required": true
      },
      {
        "id": "conductive-reflect-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete your Circuit Sculptor reflection",
        "complete_title": "Reflection complete",
        "incomplete_prompt": "Explain what changed from your opening theory and what you would investigate next.",
        "complete_prompt": "You showed how evidence changed your thinking. Next, Grow the design beyond the repaired landmark.",
        "rules": [
          {
            "field": "final_reflection",
            "condition": "min_length",
            "value": 45
          }
        ]
      }
    ]
  },
  "conductive-dough-grow": {
    "step_id": "conductive-dough-grow",
    "title": "Grow the Glow City Design",
    "subtitle": "Transfer the Rules, Then Choose Whether to Continue",
    "phase": "Grow",
    "story": "Transfer the circuit rules to a new soft-circuit idea, then choose whether you want to continue independently with a three-trial Glow City challenge.",
    "self_guided": {
      "title": "Invent a new Glow City question",
      "say": "You have restored the core mission. Extending it is a choice, not a reason to rush into another powered build.",
      "do_now": "Describe a new soft-circuit idea and a fair test plan. For an optional extension, use only kit-specified materials and repeat the power-off safety checks. Ask Professor Swan to review your plan; get adult help if it needs extra tools or unfamiliar components.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You completed the guided Glow City mission and wrote how you might use its circuit ideas in a new challenge. The independent extension is optional.",
      "next_preview": "Mission complete. If you wish, explain one Glow City design rule and the evidence that supports it."
    },
    "self_guided_block_guidance": {
      "conductive-grow-transfer": {
        "title": "Plan another glowing design",
        "try_now": "Describe the new idea first. Ask Professor Swan to review your fair-test plan and complete the kit safety checks before any physical extension."
      }
    },
    "blocks": [
      {
        "id": "conductive-grow-story",
        "type": "story_hook",
        "eyebrow": "GROW",
        "title": "A repaired city is the start of the next design",
        "body": "The same rules—complete paths, LED direction, separated conductive routes, and fair testing—can guide many new soft-circuit designs.",
        "tone": "mystery"
      },
      {
        "id": "conductive-grow-transfer",
        "type": "evidence_response",
        "title": "Design the next Glow City idea",
        "prompt": "Describe one new soft-circuit landmark or feature. Which circuit rule will matter most, and what evidence would show that your idea works reliably?",
        "placeholder": "My next design would... The rule that matters is... I would collect evidence by...",
        "save_as": "conductive_grow_transfer",
        "min_length": 40,
        "required": true
      },
      {
        "id": "conductive-grow-saras",
        "type": "saras_checkpoint",
        "title": "Extend the Glow City rules with Saras",
        "suggested_questions": [
          "Does my new design still have a complete current path?",
          "Where should conductive and insulating dough have different jobs?",
          "What would make my next test fair?"
        ],
        "hidden_context": "The learner completed the guided Conductive Dough STEM Mission. Use the accurate four-AA kit model: conductive dough can carry current, insulating dough has a different high-resistance role, LED polarity matters, and unintended low-resistance bypasses can divert current. Do not recommend adding liquids or inventing measurements. Coach the learner to transfer the rules to a new design.",
        "reveal_policy": "The full Conductive Dough science may be discussed. Preserve learner authorship and require evidence."
      },
      {
        "id": "conductive-grow-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the Grow transfer",
        "complete_title": "Guided Mission complete",
        "incomplete_prompt": "Apply the Glow City rules to one new design idea.",
        "complete_prompt": "The guided Mission is complete. The independent challenge below is optional and does not gate completion.",
        "rules": [
          {
            "field": "conductive_grow_transfer",
            "condition": "min_length",
            "value": 40
          }
        ]
      },
      {
        "id": "after-camp-glow-city-mission",
        "type": "story_hook",
        "eyebrow": "YOUR MISSION CONTINUES",
        "title": "Keep Building After the Guided Mission",
        "body": "Set aside about 60–90 minutes later today. Work independently to improve, rebuild, or troubleshoot a Glow City landmark. Plan before changing anything, switch the power off before every adjustment, and use evidence from three trials rather than stopping after one success.",
        "tone": "mystery",
        "instructional_mode": "you_do",
        "student_cue": "Complete this mission independently after the guided Mission. Your guided Mission is already complete; this is your next engineering challenge."
      },
      {
        "id": "after-camp-mission-choice",
        "type": "choice_cards",
        "title": "Choose your independent Glow City challenge",
        "prompt": "Choose one mission that will require planning, testing, and evidence. You may do more than one if time allows.",
        "choices": [
          {
            "id": "reliability",
            "label": "Reliability challenge — make one landmark glow clearly in three separate on/off trials"
          },
          {
            "id": "new_landmark",
            "label": "New landmark challenge — create a different sculpture while preserving the electrical roads, walls, and light-gate rules"
          },
          {
            "id": "controlled_redesign",
            "label": "Controlled redesign challenge — change one feature and test whether it improves stability or lighting reliability"
          },
          {
            "id": "safe_troubleshooting",
            "label": "Safe troubleshooting challenge — diagnose and repair a naturally dark, dim, or flickering landmark without deliberately creating a short circuit"
          }
        ],
        "save_as": "after_camp_mission_choice",
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Select the challenge you will own from planning through evidence and explanation."
      },
      {
        "id": "after-camp-plan",
        "type": "experiment_record",
        "title": "Write your plan before changing the landmark",
        "prompt": "A strong independent engineer decides what will change, what will stay the same, and what evidence will count as success.",
        "save_as": "after_camp_plan",
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Complete the plan in your own words before touching the circuit or dough.",
        "fields": [
          {
            "id": "goal",
            "label": "My mission goal",
            "type": "text",
            "placeholder": "My goal is to...",
            "min_length": 20
          },
          {
            "id": "one_change",
            "label": "The one main feature I will change or investigate",
            "type": "text",
            "placeholder": "I will change or investigate...",
            "min_length": 20
          },
          {
            "id": "keep_same",
            "label": "What I will keep the same for a fair comparison",
            "type": "text",
            "placeholder": "I will keep... the same because...",
            "min_length": 20
          },
          {
            "id": "prediction",
            "label": "My prediction and reason",
            "type": "text",
            "placeholder": "I predict... because...",
            "min_length": 30
          },
          {
            "id": "success_evidence",
            "label": "What evidence will show that the mission succeeded?",
            "type": "text",
            "placeholder": "I will count the mission as successful when...",
            "min_length": 25
          }
        ]
      },
      {
        "id": "after-camp-safety-check",
        "type": "multi_select",
        "title": "Complete the independent-work safety check",
        "prompt": "Check each statement only when you are ready to follow it during every trial.",
        "choices": [
          {
            "id": "power_off",
            "label": "I will switch the battery holder OFF before touching or changing the circuit"
          },
          {
            "id": "no_deliberate_short",
            "label": "I will not deliberately press conductive paths together or create a direct short circuit"
          },
          {
            "id": "separate_paths",
            "label": "I will inspect whether conductive paths that should be separate are actually separated"
          },
          {
            "id": "approved_materials",
            "label": "I will use only the supplied or teacher-approved materials and will not add liquids"
          },
          {
            "id": "brief_power_tests",
            "label": "I will use brief power-on tests and switch OFF before making the next adjustment"
          }
        ],
        "save_as": "after_camp_safety_checks",
        "min_selections": 5,
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Take responsibility for safe testing before you begin the three trials."
      },
      {
        "id": "after-camp-three-trials",
        "type": "experiment_record",
        "title": "Run and record three independent trials",
        "prompt": "Use the same success rule each time. Do not write only “worked” or “did not work”—record visible evidence.",
        "save_as": "after_camp_three_trials",
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Test three times, record what actually happened, and make only power-off adjustments between trials.",
        "fields": [
          {
            "id": "trial_one",
            "label": "Trial 1 — setup, result, and visible evidence",
            "type": "text",
            "placeholder": "In Trial 1, I... The light... I observed...",
            "min_length": 35
          },
          {
            "id": "between_one_two",
            "label": "Power-off adjustment before Trial 2, or why no adjustment was needed",
            "type": "text",
            "placeholder": "Before Trial 2, I switched off the power and...",
            "min_length": 25
          },
          {
            "id": "trial_two",
            "label": "Trial 2 — setup, result, and visible evidence",
            "type": "text",
            "placeholder": "In Trial 2, I... The light... I observed...",
            "min_length": 35
          },
          {
            "id": "between_two_three",
            "label": "Power-off adjustment before Trial 3, or why no adjustment was needed",
            "type": "text",
            "placeholder": "Before Trial 3, I switched off the power and...",
            "min_length": 25
          },
          {
            "id": "trial_three",
            "label": "Trial 3 — setup, result, and visible evidence",
            "type": "text",
            "placeholder": "In Trial 3, I... The light... I observed...",
            "min_length": 35
          }
        ]
      },
      {
        "id": "after-camp-evidence-conclusion",
        "type": "evidence_response",
        "title": "Write your independent engineering conclusion",
        "prompt": "Compare all three trials. Explain whether the change helped, caused a tradeoff, or needs more evidence. Connect your result to at least two Glow City ideas: electrical roads, insulating walls, LED direction, complete paths, or shortcut prevention.",
        "placeholder": "Across the three trials, I found... The strongest evidence was... This connects to... My next test would be...",
        "save_as": "after_camp_evidence_conclusion",
        "min_length": 80,
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Use your own trial evidence to decide what you can honestly claim."
      },
      {
        "id": "after-camp-saras",
        "type": "saras_checkpoint",
        "title": "Challenge your independent conclusion with Saras",
        "suggested_questions": [
          "Ask me to defend my conclusion using evidence from all three trials.",
          "Did I really change only one main feature?",
          "Which Glow City rule best explains my result?",
          "What would be the fairest next test?"
        ],
        "hidden_context": "The student is completing the optional independent Glow City challenge after the guided nine-phase STEM Mission. Use after_camp_mission_choice, after_camp_plan, after_camp_safety_checks, after_camp_three_trials, and after_camp_evidence_conclusion. Ask for specific evidence from all three trials. Check whether the student changed one main feature and kept comparison conditions reasonably consistent. Reinforce switching power off before adjustments, avoiding deliberate shorts, keeping conductive paths separated, using only approved materials, and not adding liquids. Coach the student's reasoning without rewriting the conclusion.",
        "reveal_policy": "The full Conductive Dough science may be discussed. Emphasize evidence, fair comparison, safe power-off adjustments, and honest limits on the conclusion.",
        "instructional_mode": "you_do",
        "student_cue": "Use Saras as a coach after you have written your own conclusion."
      }
    ]
  }
};

export const getConductiveDoughMissionPage = (stepId) =>
  CONDUCTIVE_DOUGH_MISSION_PAGES[String(stepId || "")] || null;

export default CONDUCTIVE_DOUGH_MISSION_PAGES;
